export const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
export const ALLOWED_TYPES = [
  '.pdf',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
  '.xlsx',
  '.xls',
  '.ppt',
  '.pptx',
  '.zip',
  '.csv',
]

export interface FileValidationResult {
  validFiles: File[]
  errors: string[]
}

export interface FileValidationOptions {
  maxFiles?: number
  currentFileCount?: number
  stage?: string
  currentStageCounts?: Record<string, number>
}

export const STAGE_LIMITS = {
  author: 1,
  assessor: 1,
  joint_review: 2,
} as const

export function validateFiles(
  files: File[],
  options: FileValidationOptions = {}
): FileValidationResult {
  const {
    maxFiles,
    currentFileCount = 0,
    stage,
    currentStageCounts = {},
  } = options
  const validationErrors: string[] = []
  const validFiles: File[] = []

  if (stage && STAGE_LIMITS[stage as keyof typeof STAGE_LIMITS]) {
    const stageLimit = STAGE_LIMITS[stage as keyof typeof STAGE_LIMITS]
    const currentStageCount = currentStageCounts[stage] || 0

    if (currentStageCount + files.length > stageLimit) {
      const remainingSlots = stageLimit - currentStageCount
      if (remainingSlots <= 0) {
        validationErrors.push(
          `Maximum ${stageLimit} attachment${
            stageLimit === 1 ? '' : 's'
          } allowed. You already have ${currentStageCount} attachment${
            currentStageCount === 1 ? '' : 's'
          }.`
        )
        return { validFiles: [], errors: validationErrors }
      } else {
        validationErrors.push(
          `Maximum ${stageLimit} attachment${
            stageLimit === 1 ? '' : 's'
          } allowed. You can only add ${remainingSlots} more attachment${
            remainingSlots === 1 ? '' : 's'
          }.`
        )
        files = files.slice(0, remainingSlots)
      }
    }
  }

  if (maxFiles && currentFileCount + files.length > maxFiles) {
    const remainingSlots = maxFiles - currentFileCount
    if (remainingSlots <= 0) {
      validationErrors.push(
        `Maximum of ${maxFiles} files allowed. You already have ${currentFileCount} files.`
      )
      return { validFiles: [], errors: validationErrors }
    } else {
      validationErrors.push(
        `Maximum of ${maxFiles} files allowed. You can only add ${remainingSlots} more file${
          remainingSlots === 1 ? '' : 's'
        }.`
      )
      files = files.slice(0, remainingSlots)
    }
  }

  files.forEach((file) => {
    if (file.size > MAX_FILE_SIZE) {
      validationErrors.push(
        `File size exceeds 10MB limit (${(file.size / 1024 / 1024).toFixed(
          1
        )}MB)`
      )
      return
    }

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_TYPES.includes(fileExtension || '')) {
      validationErrors.push(
        `File type not supported. Allowed types: ${ALLOWED_TYPES.join(', ')}`
      )
      return
    }

    validFiles.push(file)
  })

  return {
    validFiles,
    errors: validationErrors,
  }
}

export function formatValidationErrors(errors: string[]): string {
  return errors.join('\n')
}
